import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../services/api';
import './InvoiceForm.css';

function InvoiceForm({ user }) {
  const [formData, setFormData] = useState({
    // Datos de AFIP/ARCA
    invoiceNumber: '',
    puntoVenta: '',
    tipoComprobante: '6', // Default: Factura B
    
    // Datos del proveedor
    supplierCuit: '',
    supplierName: '',
    
    // Datos de la factura
    invoiceDate: '',
    totalAmount: '',
    currency: 'ARS',
    
    // Items de línea (ejemplo)
    items: [],
    
    // Campos adicionales
    additionalFields: {}
  });

  const [newItem, setNewItem] = useState({
    description: '',
    quantity: '',
    unitPrice: '',
    total: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  if (!user?.canCreateInvoices) {
    return (
      <div className="container">
        <div className="alert alert-error">No tienes permisos para crear facturas.</div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    const updatedItem = {
      ...newItem,
      [name]: value
    };
    
    // Calculate total if quantity and unitPrice are filled
    if (name === 'quantity' || name === 'unitPrice') {
      const quantity = parseFloat(updatedItem.quantity) || 0;
      const unitPrice = parseFloat(updatedItem.unitPrice) || 0;
      updatedItem.total = (quantity * unitPrice).toFixed(2);
    }
    
    setNewItem(updatedItem);
  };

  const addItem = () => {
    if (!newItem.description || !newItem.quantity || !newItem.unitPrice) {
      alert('Por favor completa todos los campos del ítem');
      return;
    }

    setFormData({
      ...formData,
      items: [...formData.items, { ...newItem }]
    });

    // Reset new item
    setNewItem({
      description: '',
      quantity: '',
      unitPrice: '',
      total: ''
    });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.invoiceNumber || !formData.puntoVenta || !formData.tipoComprobante) {
        setError('Faltan datos obligatorios de la factura');
        setLoading(false);
        return;
      }

      // Set calculated total
      const total = formData.items.length > 0 ? calculateTotal() : formData.totalAmount;
      
      const invoiceData = {
        ...formData,
        totalAmount: parseFloat(total)
      };

      const response = await invoiceService.createInvoice(invoiceData);
      navigate(`/invoices/${response.invoice.invoiceId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear la factura');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const submitForApproval = async () => {
    // First save as draft
    await handleSubmit(new Event('submit'));
    
    // Then update status to pending_approval
    // This would require an additional API call in a real implementation
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>Nueva Factura</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h2>Datos de Identificación (AFIP/ARCA)</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="tipoComprobante">Tipo de Comprobante *</label>
              <select
                id="tipoComprobante"
                name="tipoComprobante"
                className="form-control"
                value={formData.tipoComprobante}
                onChange={handleChange}
                required
              >
                <option value="1">Factura A</option>
                <option value="6">Factura B</option>
                <option value="11">Factura C</option>
                <option value="51">Factura M</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="puntoVenta">Punto de Venta *</label>
              <input
                type="text"
                id="puntoVenta"
                name="puntoVenta"
                className="form-control"
                value={formData.puntoVenta}
                onChange={handleChange}
                required
                placeholder="0001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="invoiceNumber">Número de Comprobante *</label>
              <input
                type="text"
                id="invoiceNumber"
                name="invoiceNumber"
                className="form-control"
                value={formData.invoiceNumber}
                onChange={handleChange}
                required
                placeholder="00012345"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Datos del Proveedor</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="supplierCuit">CUIT del Proveedor</label>
              <input
                type="text"
                id="supplierCuit"
                name="supplierCuit"
                className="form-control"
                value={formData.supplierCuit}
                onChange={handleChange}
                placeholder="20-12345678-9"
              />
            </div>

            <div className="form-group">
              <label htmlFor="supplierName">Nombre del Proveedor</label>
              <input
                type="text"
                id="supplierName"
                name="supplierName"
                className="form-control"
                value={formData.supplierName}
                onChange={handleChange}
                placeholder="Nombre o Razón Social"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Datos de la Factura</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="invoiceDate">Fecha de Factura</label>
              <input
                type="date"
                id="invoiceDate"
                name="invoiceDate"
                className="form-control"
                value={formData.invoiceDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency">Moneda</label>
              <select
                id="currency"
                name="currency"
                className="form-control"
                value={formData.currency}
                onChange={handleChange}
              >
                <option value="ARS">Pesos Argentinos (ARS)</option>
                <option value="USD">Dólares (USD)</option>
                <option value="EUR">Euros (EUR)</option>
              </select>
            </div>

            {formData.items.length === 0 && (
              <div className="form-group">
                <label htmlFor="totalAmount">Monto Total</label>
                <input
                  type="number"
                  id="totalAmount"
                  name="totalAmount"
                  className="form-control"
                  value={formData.totalAmount}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h2>Items de Factura</h2>
          
          {formData.items.length > 0 && (
            <table className="table" style={{ marginBottom: '20px' }}>
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>${parseFloat(item.unitPrice).toFixed(2)}</td>
                    <td>${parseFloat(item.total).toFixed(2)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="btn btn-danger btn-sm"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold' }}>
                  <td colSpan="3" style={{ textAlign: 'right' }}>Total:</td>
                  <td>${calculateTotal()}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}

          <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>Agregar Ítem</h3>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <input
                type="text"
                name="description"
                className="form-control"
                value={newItem.description}
                onChange={handleItemChange}
                placeholder="Descripción"
              />
            </div>
            <div className="form-group">
              <input
                type="number"
                name="quantity"
                className="form-control"
                value={newItem.quantity}
                onChange={handleItemChange}
                placeholder="Cantidad"
                step="1"
              />
            </div>
            <div className="form-group">
              <input
                type="number"
                name="unitPrice"
                className="form-control"
                value={newItem.unitPrice}
                onChange={handleItemChange}
                placeholder="Precio Unit."
                step="0.01"
              />
            </div>
            <div className="form-group">
              <input
                type="number"
                className="form-control"
                value={newItem.total}
                placeholder="Total"
                readOnly
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <button
              type="button"
              onClick={addItem}
              className="btn btn-secondary"
            >
              Agregar
            </button>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar como Borrador'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default InvoiceForm;
